import {
  createAhpChatStartRequestWithProvisioningTarget,
  type AhpProvisioningTarget,
  type AhpSpaceSelection,
  type DirectTurnTelemetrySeed,
} from '@github-ui/ahp-session-client/contracts'
import {getEnv} from '@github-ui/client-env'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import type {EmbeddedAccount} from '@github-ui/copilot-chat-app-next/EmbeddedChatTypes'
import type {
  NewSessionComposerHandle,
  NewSessionComposerProps,
  NewSessionStart,
} from '@github-ui/copilot-chat-app-next/NewSessionComposer'
import {NewSessionComposerPlaceholder} from '@github-ui/copilot-chat-app-next/NewSessionComposerPlaceholder'
import type {ChatInputRef} from '@github-ui/copilot-shared-ui/components/chat-input-ref'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {SANDBOX_ENVIRONMENT_ID} from '@github-ui/one-copilot-composer/environment-strip/environment-option'
import type {
  ComposerHandle,
  NewCopilotComposerTelemetry,
  NewSessionConfiguration,
  NewSessionController,
  NewSessionInitialInput,
  NewSessionTarget,
} from '@github-ui/one-copilot-composer/new'
import {
  getDraftStorageKeySuffix,
  isOrganizationSandboxEnabled,
  resolveMcpPermissionGate,
  resolveMcpPermissionMode,
} from '@github-ui/one-copilot-composer/shared'
import {lazy, Suspense, useImperativeHandle, useRef, useState, type ReactNode, type Ref, type RefObject} from 'react'

const NewSessionComposer = lazy(async () => {
  const module = await import('@github-ui/copilot-chat-app-next/NewSessionComposer')

  return {default: module.NewSessionComposer}
})

type StartRepository = NonNullable<NewSessionStart['repository']>

function isEnvironmentsEnabled() {
  return isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
}

function isCloudStartable({createTask, createEnvironmentTask, chatLaunch}: NewSessionController) {
  if (chatLaunch?.kind === 'handoff') return true
  if (!createEnvironmentTask) return false

  return !createTask || chatLaunch !== undefined || isEnvironmentsEnabled()
}

// Matches legacy: hosts that bind environments themselves (not a Chat handoff) also list remote environments.
function isRemoteOffered({createTask, createEnvironmentTask, chatLaunch}: NewSessionController) {
  const isHandoffOnly = chatLaunch?.kind === 'handoff' && !chatLaunch.submitRepositoryAsChat

  return isEnvironmentsEnabled() && Boolean(createTask && createEnvironmentTask) && !isHandoffOnly
}

// Matches legacy: Chat targets, Cloud launches and hand-offs with attachments start on Cloud; a restoring launch
// reopens the last-picked environment; any other launch starts without one, which shows Actions.
function environmentStartOf(input: NewSessionInitialInput | undefined): NewSessionComposerProps['environmentStart'] {
  if (!input) return 'none'
  const hasAttachments = input.target.kind === 'task' && Boolean(input.prompt?.attachments?.length)
  if (input.initialEnvironment === 'sandbox' || input.target.kind === 'chat' || hasAttachments) return 'cloud'

  return input.restoreLastEnvironment ? 'restore' : 'none'
}

function hostModePickerOf({
  modePicker,
  mode,
  mcpPermissionGateEnabled,
}: NewSessionConfiguration): NewSessionComposerProps['modePicker'] {
  if (modePicker !== undefined) return 'agent'

  return mode === undefined && resolveMcpPermissionGate(mcpPermissionGateEnabled) ? 'mcp-permission' : 'none'
}

function isDirectTurnTrackedFor(
  {chatLaunch}: NewSessionController,
  {directTurnTelemetrySurface, preserveDirectTurnTelemetryForHandoff}: NewSessionConfiguration,
  initialSeed: DirectTurnTelemetrySeed | undefined,
) {
  const isLaunchTracked = chatLaunch?.kind !== 'handoff' || Boolean(preserveDirectTurnTelemetryForHandoff)

  return isLaunchTracked && (initialSeed !== undefined || directTurnTelemetrySurface !== undefined)
}

function directTurnSeedOf(
  {directTurn}: NewSessionStart,
  {directTurnTelemetrySurface}: NewSessionConfiguration,
  initialSeed: DirectTurnTelemetrySeed | undefined,
): DirectTurnTelemetrySeed | undefined {
  if (!directTurn) return undefined
  if (initialSeed) return initialSeed

  return directTurnTelemetrySurface
    ? {requestId: directTurn.requestId, timing: directTurn.timing, surface: directTurnTelemetrySurface}
    : undefined
}

function isEnvironmentShown({createTask, createEnvironmentTask, chatLaunch}: NewSessionController) {
  const isOfferedByHandoff =
    chatLaunch?.kind === 'handoff' && !chatLaunch.submitRepositoryAsChat && Boolean(createTask && createEnvironmentTask)

  return !createTask || isOfferedByHandoff || (isEnvironmentsEnabled() && Boolean(createEnvironmentTask))
}

function accountOf(
  configuration: NewSessionConfiguration,
  controller: NewSessionController,
  isReadOnly: boolean,
): EmbeddedAccount {
  return {
    login: configuration.currentUserLogin ?? getEnv().login ?? '',
    avatarUrl: configuration.currentUserAvatarUrl,
    organizations: (configuration.availableOrganizations ?? []).map(organization => ({
      id: organization.id,
      login: organization.login,
      avatarUrl: organization.avatarUrl,
      isSsoRequired: organization.requiresSso,
      isSandboxEnabled: isOrganizationSandboxEnabled(organization),
    })),
    managedSettings: configuration.enterpriseManagedSettings,
    managedSettingsUrl: configuration.enterpriseManagedSettingsUrl,
    isSandboxEnabled: configuration.supportsManagedSandbox !== false && isCloudStartable(controller),
    isReadOnly,
  }
}

type TargetRepository = NonNullable<Extract<NewSessionTarget, {kind: 'task'}>['repository']>

function nameWithOwnerOf(repository: TargetRepository) {
  if (repository.kind === 'name') return repository.nameWithOwner
  const {nameWithOwner, ownerLogin, name} = repository.repository

  return nameWithOwner ?? (ownerLogin && name ? `${ownerLogin}/${name}` : undefined)
}

function initialRepositoryOf(target: NewSessionTarget | undefined) {
  if (target?.kind === 'chat') {
    const provisioning = target.provisioningTarget

    return provisioning?.kind === 'repository' ? {nameWithOwner: provisioning.nameWithOwner} : undefined
  }

  const repository = target?.repository
  const nameWithOwner = repository ? nameWithOwnerOf(repository) : undefined
  if (!repository || !nameWithOwner) return undefined

  return {nameWithOwner, ...(repository.branch ? {branch: repository.branch} : {})}
}

function draftScopeOf(target: NewSessionTarget | undefined, draftSource: string) {
  if (target?.kind === 'chat') return 'chat'
  if (draftSource === 'agents_panel') return undefined
  const repository = target?.repository
  const lastUsed = copilotLocalStorage.getLastUsedRepository()

  return (
    (repository ? nameWithOwnerOf(repository) : undefined) ??
    (lastUsed ? `${lastUsed.ownerLogin}/${lastUsed.name}` : undefined)
  )
}

function initialSpaceOf(target: NewSessionTarget | undefined): AhpSpaceSelection | undefined {
  if (target?.kind !== 'chat') return undefined
  if (target.provisioningTarget?.kind === 'space') return target.provisioningTarget.space

  return target.context?.kind === 'space' ? target.context.space : undefined
}

function ownerOf(repository: StartRepository) {
  return repository.nameWithOwner.split('/')[0] ?? ''
}

function spaceOf(space: AhpSpaceSelection | undefined) {
  return space ? {space: {owner: space.owner, slug: space.slug}, initialSelectedSpace: space} : {}
}

function provisioningTargetOf({repository, scope}: NewSessionStart): AhpProvisioningTarget | undefined {
  if (repository) return {kind: 'repository', nameWithOwner: repository.nameWithOwner, scopeOwner: ownerOf(repository)}

  return scope ? {kind: 'scope', owner: scope} : undefined
}

async function startActionsTask(createTask: NewSessionController['createTask'], start: NewSessionStart) {
  const {prompt, mode, repository, customAgent, agent} = start
  if (!createTask || !repository) throw new Error('This page can’t start an Actions task.')
  const reasoningEffort = prompt.model?.config?.reasoningEffort
  const mcpPermissionMode = resolveMcpPermissionMode({agentMode: mode, fallback: start.mcpPermissionMode})

  await createTask(
    prompt.text,
    {
      name: repository.nameWithOwner,
      nameOnly: repository.nameWithOwner.split('/')[1] ?? '',
      nameWithOwner: repository.nameWithOwner,
      owner: ownerOf(repository),
      enabled: true,
      defaultBranch: repository.branch,
      private: false,
      isEmpty: false,
    },
    repository.branch,
    customAgent
      ? {typeName: 'customSubAgent', subAgentSlug: customAgent.slug, displayName: customAgent.displayName}
      : agent?.kind === 'top-level'
        ? {
            typeName: 'topLevel',
            topLevelAgentId: agent.id,
            displayName: agent.displayName,
            avatarUrl: agent.avatarUrl,
            login: agent.slug,
            modelVendors: agent.modelVendors,
          }
        : {},
    prompt.model?.id,
    {
      ...(prompt.attachments?.length ? {attachments: prompt.attachments} : {}),
      ...(isReasoningEffort(reasoningEffort) ? {reasoningEffort} : {}),
      ...(mcpPermissionMode === 'allow_all' ? {mcpPermissionMode} : {}),
    },
  )
}

async function startChatHandoff(
  onSubmit: Extract<NewSessionController['chatLaunch'], {kind: 'handoff'}>['onSubmit'],
  start: NewSessionStart,
  seed: DirectTurnTelemetrySeed | undefined,
) {
  const request = createAhpChatStartRequestWithProvisioningTarget({
    prompt: start.prompt,
    target: provisioningTargetOf(start),
    ...spaceOf(start.prompt.displaySpace),
  })

  await onSubmit(
    start.repository
      ? {...request, metadata: {...request.metadata, repository: start.repository.nameWithOwner}}
      : request,
    seed,
    start.approvalMode,
  )
}

async function startCloudSession(
  controller: NewSessionController,
  start: NewSessionStart,
  seed: DirectTurnTelemetrySeed | undefined,
) {
  const {prompt, mode, approvalMode, repository, scope, customAgent} = start
  if (!controller.createEnvironmentTask) throw new Error('This page can’t start a Cloud session.')
  const isChatLaunch = !repository && controller.chatLaunch?.kind === 'environment'
  const scopeLogin = repository ? ownerOf(repository) : scope

  await controller.createEnvironmentTask({
    environmentId: SANDBOX_ENVIRONMENT_ID,
    prompt,
    managedSandbox: true,
    mcpPermissionMode: resolveMcpPermissionMode({approvalMode, agentMode: mode, fallback: start.mcpPermissionMode}),
    ...(customAgent ? {customAgent} : {}),
    ...(mode ? {agentMode: mode} : {}),
    ...(approvalMode ? {approvalMode} : {}),
    ...(repository ? {repositoryNwo: repository.nameWithOwner, baseRef: repository.branch} : {}),
    ...(isChatLaunch ? {chatLaunch: true, failureOwner: 'caller' as const} : {}),
    ...(isChatLaunch && scope ? {provisioningTarget: {kind: 'scope' as const, owner: scope}} : {}),
    ...(scopeLogin ? {scopeLogin} : {}),
    ...spaceOf(prompt.displaySpace),
    ...(seed ? {directTurnTelemetrySeed: seed} : {}),
  })
}

// Matches legacy: a remote environment is bound by id, without a managed sandbox or scope.
async function startRemoteSession(
  createEnvironmentTask: NewSessionController['createEnvironmentTask'],
  {
    prompt,
    mode,
    mcpPermissionMode: fallback,
    approvalMode,
    repository,
    customAgent,
    remoteEnvironmentId,
  }: NewSessionStart,
) {
  if (!createEnvironmentTask || !remoteEnvironmentId) throw new Error('This page can’t start a remote session.')
  const mcpPermissionMode = resolveMcpPermissionMode({approvalMode, agentMode: mode, fallback})

  await createEnvironmentTask({
    environmentId: remoteEnvironmentId,
    prompt,
    ...(customAgent ? {customAgent} : {}),
    ...(mode ? {agentMode: mode} : {}),
    ...(approvalMode ? {approvalMode} : {}),
    ...(repository ? {repositoryNwo: repository.nameWithOwner, baseRef: repository.branch} : {}),
    ...(mcpPermissionMode === 'allow_all' ? {mcpPermissionMode} : {}),
  })
}

function startWith(
  controller: NewSessionController,
  start: NewSessionStart,
  seed: DirectTurnTelemetrySeed | undefined,
) {
  if (start.environment === 'actions') return startActionsTask(controller.createTask, start)
  if (start.environment === 'remote') return startRemoteSession(controller.createEnvironmentTask, start)
  const launch = controller.chatLaunch
  if (launch?.kind === 'handoff' && (!start.repository || launch.submitRepositoryAsChat)) {
    return startChatHandoff(launch.onSubmit, start, seed)
  }

  return startCloudSession(controller, start, seed)
}

export interface RewrittenNewSessionComposerProps {
  controller: NewSessionController
  configuration: NewSessionConfiguration
  telemetry?: NewCopilotComposerTelemetry
  initialInput?: NewSessionInitialInput
  isReadOnly?: boolean
  isNewConversation?: boolean
  draftSource?: string
  modePicker?: NewSessionComposerProps['modePicker']
  environmentStart?: NewSessionComposerProps['environmentStart']
  isOwnerScoped?: boolean
  isDraggedOver?: boolean
  codeReference?: NewSessionComposerProps['codeReference']
  chatPlaceholder?: string
  taskPlaceholder?: string
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
  chatInputRef?: RefObject<ChatInputRef | null>
  handleRef?: Ref<ComposerHandle>
  children?: ReactNode
}

/**
 * The rewritten new-session composer behind `copilot_chat_surfaces_rewrite`, started through the same
 * `controller` the legacy composer receives.
 */
export function RewrittenNewSessionComposer({
  controller,
  configuration,
  telemetry,
  initialInput,
  isReadOnly = false,
  isNewConversation,
  draftSource,
  modePicker,
  environmentStart,
  isOwnerScoped,
  isDraggedOver,
  codeReference,
  chatPlaceholder,
  taskPlaceholder,
  textAreaRef,
  chatInputRef,
  handleRef,
  children,
}: RewrittenNewSessionComposerProps) {
  const composerRef = useRef<NewSessionComposerHandle>(null)
  const isInitialSeedUsedRef = useRef(false)
  const target = initialInput?.target
  const isSentOnOpen = initialInput?.behavior === 'submit'
  const [draftStorageKey] = useState(() =>
    draftSource === undefined
      ? undefined
      : getDraftStorageKeySuffix(draftSource, draftScopeOf(initialInput?.target, draftSource)),
  )
  const isChatShownWithoutCloud = initialInput?.target.kind === 'chat' && !isCloudStartable(controller)
  const initialSeed = isSentOnOpen ? initialInput.directTurnTelemetrySeed : undefined

  useImperativeHandle(chatInputRef, () => ({
    setText: text =>
      composerRef.current?.setText(typeof text === 'function' ? text(textAreaRef?.current?.value ?? '') : text),
  }))
  useImperativeHandle(handleRef, () => ({addFiles: files => composerRef.current?.addFiles(files)}))

  function onStart(start: NewSessionStart) {
    const seed = directTurnSeedOf(start, configuration, isInitialSeedUsedRef.current ? undefined : initialSeed)
    isInitialSeedUsedRef.current = true

    return startWith(controller, start, seed)
  }

  return (
    <Suspense
      fallback={<NewSessionComposerPlaceholder isNewConversation={isNewConversation} isReadOnly={isReadOnly} />}
    >
      <NewSessionComposer
        account={accountOf(configuration, controller, isReadOnly)}
        surface={telemetry?.surface ?? 'unknown'}
        directTurnSurface={configuration.directTurnTelemetrySurface ?? 'unknown'}
        isActionsAvailable={controller.createTask !== undefined}
        isNewConversation={isNewConversation}
        isChatOffered={controller.chatLaunch !== undefined || isChatShownWithoutCloud}
        isChatShownWithoutCloud={isChatShownWithoutCloud}
        isOwnerScoped={isOwnerScoped}
        isRemoteOffered={isRemoteOffered(controller)}
        environmentStart={environmentStart ?? environmentStartOf(initialInput)}
        isEnvironmentShown={isEnvironmentShown(controller)}
        modePicker={modePicker ?? hostModePickerOf(configuration)}
        isRepositoryLocked={target?.kind === 'task' && Boolean(target.lockRepository)}
        isStartBlocked={Boolean(configuration.disableSandboxSubmission)}
        isDirectTurnTracked={isDirectTurnTrackedFor(controller, configuration, initialSeed)}
        isDraggedOver={isDraggedOver}
        initialDraft={isSentOnOpen ? undefined : initialInput?.prompt?.text}
        draftStorageKey={draftStorageKey}
        promptToSend={isSentOnOpen ? initialInput.prompt : undefined}
        initialRepository={initialRepositoryOf(target)}
        initialSpace={initialSpaceOf(target)}
        initialApprovalMode={isSentOnOpen ? configuration.approvalMode : undefined}
        codeReference={codeReference}
        chatPlaceholder={chatPlaceholder}
        taskPlaceholder={taskPlaceholder}
        earlyProvisioning={controller.earlyProvisioning}
        textAreaRef={textAreaRef}
        handleRef={composerRef}
        onStart={onStart}
      >
        {children}
      </NewSessionComposer>
    </Suspense>
  )
}
